import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { DEFAULT_LOCALE, isLocale, LOCALES, type Locale } from '@chess-uno/shared-protocol'
import { PAGE_PATHS, localizedPath, publicPageAt } from '../seo.js'
import { de } from './de.js'
import { en } from './en.js'
import { es } from './es.js'
import { fr, type Messages } from './fr.js'
import { hi } from './hi.js'

export type { Messages }

export const CATALOGS: Record<Locale, Messages> = { fr, en, es, de, hi }

const KEY = 'chess-uno:locale'
/** Set once the visitor picked a language themselves (hides the language hint). */
const CHOSEN_KEY = 'chess-uno:locale-chosen'

function read(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    // Private browsing or blocked storage: the language is simply not remembered.
  }
}

/** Language of the browser, reduced to the supported ones (English otherwise). */
export function browserLocale(): Locale {
  if (typeof navigator === 'undefined') return DEFAULT_LOCALE
  const language = navigator.language?.toLowerCase().slice(0, 2)
  return LOCALES.find((locale) => locale === language) ?? 'en'
}

export function languageChosen(): boolean {
  return read(CHOSEN_KEY) === '1'
}

interface I18n {
  locale: Locale
  t: Messages
  /** Switches language (and moves to that version of a public page). */
  setLocale: (locale: Locale) => void
  /** Records a language choice made by following a link to another version of a page. */
  choose: (locale: Locale) => void
  /** URL of the current public page in another language (null on the app pages). */
  counterpart: (locale: Locale) => string | null
  /** Link target in the current language (public pages have one URL per language). */
  path: (path: string) => string
}

const I18nContext = createContext<I18n | null>(null)

/**
 * Current language. On an indexable page, the URL decides (/en/… is English, /de/… German…)
 * and becomes the preference; the other pages (games, settings) follow the
 * preference, or the browser language on a first visit.
 */
export function LocaleProvider({ children }: { children: ReactNode }) {
  const { pathname, search, hash } = useLocation()
  const navigate = useNavigate()
  const fromUrl = publicPageAt(pathname)?.locale ?? null
  const [preferred, setPreferred] = useState<Locale>(() => {
    const stored = read(KEY)
    return fromUrl ?? (isLocale(stored) ? stored : browserLocale())
  })
  const locale = fromUrl ?? preferred

  useEffect(() => {
    if (!fromUrl) return
    write(KEY, fromUrl)
    setPreferred(fromUrl)
  }, [fromUrl])

  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  const choose = useCallback((next: Locale) => {
    write(KEY, next)
    write(CHOSEN_KEY, '1')
    setPreferred(next)
  }, [])

  const setLocale = useCallback(
    (next: Locale) => {
      choose(next)
      const page = publicPageAt(pathname)
      if (page && page.locale !== next) navigate(`${PAGE_PATHS[page.key][next]}${search}${hash}`)
    },
    [pathname, search, hash, navigate, choose],
  )

  const counterpart = useCallback(
    (other: Locale) => {
      const page = publicPageAt(pathname)
      return page ? PAGE_PATHS[page.key][other] : null
    },
    [pathname],
  )

  const value = useMemo<I18n>(
    () => ({ locale, t: CATALOGS[locale], setLocale, choose, counterpart, path: (p: string) => localizedPath(p, locale) }),
    [locale, setLocale, choose, counterpart],
  )
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18n {
  const value = useContext(I18nContext)
  if (!value) throw new Error('useI18n must be used inside <LocaleProvider>')
  return value
}

/** Remembers that the visitor dismissed the language hint. */
export function dismissLanguageHint(): void {
  write(CHOSEN_KEY, '1')
}
